import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import tasksApi from '../../utils/tasksApi';
import notesApi from '../../utils/notesApi';
import calendarApi from '../../utils/calendarApi';
import workspaceApi from '../../utils/workspaceApi';
import { useToast } from './WorkspaceUI';

const POLL_MS = 60000;

const timeUntil = (start) => {
  const mins = Math.round((new Date(start) - Date.now()) / 60000);
  if (mins <= 0) return 'now';
  if (mins < 60) return `in ${mins} min`;
  if (mins < 1440) return `in ${Math.round(mins / 60)} h`;
  return `in ${Math.round(mins / 1440)} d`;
};

/**
 * Watches for due Task, Note and Calendar-event reminders and surfaces them as
 * toasts.
 *
 * Renders nothing. Each reminder is announced once per session; task/note
 * dismissals mark `reminderDone` on the server so they never come back, while
 * calendar occurrences are de-duped in memory. Calendar reminders honour the
 * "Event reminders" workspace setting.
 */
const ReminderWatcher = ({ enabled = true }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const announced = useRef(new Set());
  const eventRemindersOn = useRef(true);

  // Read the user's notification preference once; default to on if unavailable.
  useEffect(() => {
    if (!enabled) return;
    workspaceApi
      .getSettings?.()
      .then((res) => {
        if (res?.success && res.data?.notifications) {
          eventRemindersOn.current = res.data.notifications.eventReminders !== false;
        }
      })
      .catch(() => {});
  }, [enabled]);

  const check = useCallback(async () => {
    if (document.hidden) return;

    const [tasks, notes, calendar] = await Promise.allSettled([
      tasksApi.dueReminders(),
      notesApi.dueReminders(),
      eventRemindersOn.current ? calendarApi.dueReminders() : Promise.resolve(null),
    ]);

    const show = (id, message, open, dismiss) => {
      if (announced.current.has(id)) return;
      announced.current.add(id);
      toast(message, {
        tone: 'warning',
        duration: 0,
        action: {
          label: 'Open',
          onClick: () => {
            dismiss();
            open();
          },
        },
      });
    };

    if (tasks.status === 'fulfilled' && tasks.value?.success) {
      (tasks.value.data || []).forEach((task) => {
        show(
          `task:${task._id}`,
          `⏰ Task due: ${task.title}`,
          () => navigate(`/tasks?task=${task._id}`),
          () => tasksApi.update(task._id, { reminderDone: true }).catch(() => {})
        );
      });
    }

    if (notes.status === 'fulfilled' && notes.value?.success) {
      (notes.value.data || []).forEach((note) => {
        show(
          `note:${note._id}`,
          `⏰ Note reminder: ${note.title || 'Untitled note'}`,
          () => navigate(`/notes?note=${note._id}`),
          () => notesApi.patch(note._id, { reminderDone: true }).catch(() => {})
        );
      });
    }

    if (calendar?.status === 'fulfilled' && calendar.value?.success) {
      (calendar.value.data || []).forEach((event) => {
        show(
          `event:${event._id}:${event.minutes}`,
          `📅 ${event.title} starts ${timeUntil(event.start)}`,
          () => navigate(`/calendar?event=${encodeURIComponent(event.seriesId || event._id)}`),
          () => {}
        );
      });
    }
  }, [toast, navigate]);

  useEffect(() => {
    if (!enabled) return undefined;
    // Give the app a moment to settle before the first network round trip.
    const first = window.setTimeout(check, 4000);
    const timer = window.setInterval(check, POLL_MS);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(timer);
    };
  }, [enabled, check]);

  return null;
};

export default ReminderWatcher;
